import { useState, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Lock, LogOut, Users, Download, Search, ArrowUpDown, AlertTriangle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function findDuplicateEmails(registrations) {
  const emailCount = {};
  registrations.forEach((r) => {
    const email = (r.email || "").toLowerCase().trim();
    if (email) emailCount[email] = (emailCount[email] || 0) + 1;
  });
  const dupes = new Set();
  Object.entries(emailCount).forEach(([email, count]) => {
    if (count > 1) dupes.add(email);
  });
  return dupes;
}

function findDuplicateNames(registrations) {
  const nameCount = {};
  registrations.forEach((r) => {
    const name = `${r.first_name} ${r.last_name}`.toLowerCase().trim();
    if (name) nameCount[name] = (nameCount[name] || 0) + 1;
  });
  const dupes = new Set();
  Object.entries(nameCount).forEach(([name, count]) => {
    if (count > 1) dupes.add(name);
  });
  return dupes;
}

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/verify`, { password });
      if (res.data.verified) {
        setAuthenticated(true);
        fetchRegistrations();
      }
    } catch {
      setError("Incorrect password. Please try again.");
    }
  };

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/registrations`);
      setRegistrations(res.data);
    } catch {
      setError("Failed to load registrations.");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const duplicateEmails = useMemo(() => findDuplicateEmails(registrations), [registrations]);
  const duplicateNames = useMemo(() => findDuplicateNames(registrations), [registrations]);
  const totalDuplicates = duplicateEmails.size + duplicateNames.size;

  const filtered = useMemo(() => {
    let data = [...registrations];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(
        (r) =>
          `${r.first_name} ${r.last_name}`.toLowerCase().includes(term) ||
          (r.email || "").toLowerCase().includes(term) ||
          (r.location || "").toLowerCase().includes(term) ||
          (r.other_names || "").toLowerCase().includes(term) ||
          (r.comments || "").toLowerCase().includes(term)
      );
    }
    data.sort((a, b) => {
      let aVal = a[sortField] || "";
      let bVal = b[sortField] || "";
      if (sortField === "num_people") {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [registrations, searchTerm, sortField, sortDir]);

  const stats = useMemo(() => {
    const totalPeople = registrations.reduce((sum, r) => sum + (r.num_people || 1), 0);
    const helpers = registrations.filter((r) => r.interested_helping).length;
    const attending = registrations.filter((r) => r.interested_attending === "yes").length;
    const maybe = registrations.filter((r) => r.interested_attending === "maybe").length;
    return { totalPeople, helpers, attending, maybe };
  }, [registrations]);

  const exportCSV = () => {
    if (!registrations.length) return;
    const headers = [
      "First Name", "Last Name", "Email", "Mobile", "Family Branch", "Location",
      "Attending", "Event", "Group Size", "Other Names", "Updates", "Helping", "Comments", "Date"
    ];
    const rows = registrations.map((r) => [
      `"${(r.first_name || "").replace(/"/g, '""')}"`,
      `"${(r.last_name || "").replace(/"/g, '""')}"`,
      r.email,
      r.mobile || "",
      r.family_branch || "",
      `"${(r.location || "").replace(/"/g, '""')}"`,
      r.interested_attending,
      r.which_event,
      r.num_people,
      `"${(r.other_names || "").replace(/"/g, '""')}"`,
      r.want_updates ? "Yes" : "No",
      r.interested_helping ? "Yes" : "No",
      `"${(r.comments || "").replace(/"/g, '""')}"`,
      r.created_at?.split("T")[0] || ""
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mccartney-registrations.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const isDuplicate = (r) => {
    const email = (r.email || "").toLowerCase().trim();
    const name = `${r.first_name} ${r.last_name}`.toLowerCase().trim();
    return duplicateEmails.has(email) || duplicateNames.has(name);
  };

  const SortHeader = ({ field, children }) => (
    <TableHead
      className="font-sans font-semibold text-charcoal text-sm cursor-pointer select-none hover:text-forest"
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown size={12} className={sortField === field ? "text-forest" : "text-charcoal/30"} />
      </span>
    </TableHead>
  );

  if (!authenticated) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-paper">
        <div className="max-w-sm w-full mx-auto px-6">
          <div className="text-center mb-8">
            <Lock size={32} className="text-forest mx-auto mb-4" />
            <h1
              data-testid="admin-login-heading"
              className="font-serif text-2xl sm:text-3xl font-semibold text-charcoal mb-2"
            >
              Organiser Access
            </h1>
            <p className="text-base font-sans text-charcoal/60">
              Enter the organiser password to view registrations.
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-base font-sans font-medium text-charcoal">
                Password
              </Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="h-12 text-base bg-paper border-sand"
              />
            </div>
            {error && (
              <p data-testid="admin-error-message" className="text-sm text-red-600 font-sans">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="admin-login-button"
              className="w-full bg-forest hover:bg-forest/90 text-paper font-sans text-base py-5 h-auto"
            >
              View Registrations
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1
              data-testid="admin-dashboard-heading"
              className="font-serif text-2xl sm:text-3xl font-semibold text-charcoal mb-1"
            >
              Registrations
            </h1>
            <div className="flex items-center gap-2 text-charcoal/60 font-sans text-base">
              <Users size={16} />
              <span>{registrations.length} registration{registrations.length !== 1 ? "s" : ""}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="admin-export-button"
              variant="outline"
              onClick={exportCSV}
              className="border-sand text-charcoal hover:bg-parchment font-sans text-sm"
            >
              <Download size={16} className="mr-2" />
              Export CSV
            </Button>
            <Button
              data-testid="admin-refresh-button"
              variant="outline"
              onClick={fetchRegistrations}
              className="border-sand text-charcoal hover:bg-parchment font-sans text-sm"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
            <Button
              data-testid="admin-logout-button"
              variant="outline"
              onClick={() => setAuthenticated(false)}
              className="border-sand text-charcoal hover:bg-parchment font-sans text-sm"
            >
              <LogOut size={16} className="mr-2" />
              Log Out
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        {registrations.length > 0 && (
          <div data-testid="admin-stats" className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
            <div className="bg-parchment border border-sand rounded-md p-4">
              <p className="text-sm font-sans text-charcoal/50 mb-1">Registrations</p>
              <p className="text-2xl font-serif font-semibold text-charcoal">{registrations.length}</p>
            </div>
            <div className="bg-parchment border border-sand rounded-md p-4">
              <p className="text-sm font-sans text-charcoal/50 mb-1">Total People</p>
              <p className="text-2xl font-serif font-semibold text-charcoal">{stats.totalPeople}</p>
            </div>
            <div className="bg-parchment border border-sand rounded-md p-4">
              <p className="text-sm font-sans text-charcoal/50 mb-1">Attending</p>
              <p className="text-2xl font-serif font-semibold text-forest">{stats.attending}</p>
              {stats.maybe > 0 && <p className="text-xs font-sans text-charcoal/40 mt-0.5">{stats.maybe} maybe</p>}
            </div>
            <div className="bg-parchment border border-sand rounded-md p-4">
              <p className="text-sm font-sans text-charcoal/50 mb-1">Open to Helping</p>
              <p className="text-2xl font-serif font-semibold text-bronze">{stats.helpers}</p>
            </div>
            {totalDuplicates > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                <p className="text-sm font-sans text-amber-700 mb-1 flex items-center gap-1">
                  <AlertTriangle size={14} /> Possible Duplicates
                </p>
                <p className="text-2xl font-serif font-semibold text-amber-700">{totalDuplicates}</p>
              </div>
            )}
          </div>
        )}

        {/* Search */}
        {registrations.length > 0 && (
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
              <Input
                data-testid="admin-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, location..."
                className="h-11 text-base bg-paper border-sand pl-10"
              />
            </div>
            {searchTerm && (
              <p className="text-sm font-sans text-charcoal/50 mt-2">
                Showing {filtered.length} of {registrations.length} registrations
              </p>
            )}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <p className="text-charcoal/60 font-sans text-lg py-12 text-center">Loading registrations...</p>
        ) : registrations.length === 0 ? (
          <div className="text-center py-16">
            <Users size={40} className="text-sand mx-auto mb-4" />
            <p className="text-charcoal/50 font-sans text-lg">No registrations yet.</p>
          </div>
        ) : (
          <div className="border border-sand rounded-md overflow-hidden bg-paper">
            <div className="overflow-x-auto">
              <Table data-testid="registrations-table">
                <TableHeader>
                  <TableRow className="bg-parchment">
                    <SortHeader field="first_name">Name</SortHeader>
                    <SortHeader field="email">Email</SortHeader>
                    <TableHead className="font-sans font-semibold text-charcoal text-sm">Mobile</TableHead>
                    <SortHeader field="location">Location</SortHeader>
                    <SortHeader field="interested_attending">Attending</SortHeader>
                    <SortHeader field="which_event">Event</SortHeader>
                    <SortHeader field="num_people">Group</SortHeader>
                    <TableHead className="font-sans font-semibold text-charcoal text-sm">Other Names</TableHead>
                    <TableHead className="font-sans font-semibold text-charcoal text-sm">Updates</TableHead>
                    <TableHead className="font-sans font-semibold text-charcoal text-sm">Helping</TableHead>
                    <TableHead className="font-sans font-semibold text-charcoal text-sm">Comments</TableHead>
                    <SortHeader field="created_at">Date</SortHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => {
                    const dupe = isDuplicate(r);
                    return (
                      <TableRow key={r.id} className={dupe ? "bg-amber-50/60 hover:bg-amber-50" : "hover:bg-parchment/50"}>
                        <TableCell className="font-sans text-sm font-medium text-charcoal whitespace-nowrap">
                          {dupe && <AlertTriangle size={12} className="inline text-amber-500 mr-1" />}
                          {r.first_name} {r.last_name}
                        </TableCell>
                        <TableCell className="font-sans text-sm text-charcoal/80">{r.email}</TableCell>
                        <TableCell className="font-sans text-sm text-charcoal/80">{r.mobile || "—"}</TableCell>
                        <TableCell className="font-sans text-sm text-charcoal/80">{r.family_branch || "—"}</TableCell>
                        <TableCell className="font-sans text-sm text-charcoal/80">{r.location || "—"}</TableCell>
                        <TableCell className="font-sans text-sm text-charcoal/80 capitalize">{r.interested_attending}</TableCell>
                        <TableCell className="font-sans text-sm text-charcoal/80 capitalize">{r.which_event}</TableCell>
                        <TableCell className="font-sans text-sm text-charcoal/80 text-center">{r.num_people}</TableCell>
                        <TableCell className="font-sans text-sm text-charcoal/70 max-w-[180px] truncate">{r.other_names || "—"}</TableCell>
                        <TableCell className="font-sans text-sm">
                          {r.want_updates ? (
                            <span className="text-forest font-medium">Yes</span>
                          ) : (
                            <span className="text-charcoal/40">No</span>
                          )}
                        </TableCell>
                        <TableCell className="font-sans text-sm">
                          {r.interested_helping ? (
                            <span className="text-forest font-medium">Yes</span>
                          ) : (
                            <span className="text-charcoal/40">No</span>
                          )}
                        </TableCell>
                        <TableCell className="font-sans text-sm text-charcoal/70 max-w-[180px] truncate">{r.comments || "—"}</TableCell>
                        <TableCell className="font-sans text-sm text-charcoal/60 whitespace-nowrap">
                          {r.created_at?.split("T")[0] || "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
